import { Checkbox } from "@/components/ui/checkbox";
import type { Rarity } from "@/domain/gems-game/domain/lib/Earning";
import type {
  Game,
  GameQuest,
  PredictedStatsResponse,
} from "@/domain/gems-game/domain/lib/Sweepstake";
import { formatDateOnly, formatDateStr } from "@/utils/date";
import { onDarkBase } from "@biom3/design-tokens";
import { HeroCard } from "@biom3/react";
import { Box, Link, Stack, Typography } from "@mui/material";
import { addDays, isPast } from "date-fns";
import { TrophyIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { GameQuestsCard } from "../KeyInfo/GameQuestsCard";
import { RarityText } from "../RarityText";
import { useWeeklyRewards } from "../WeeklyRewardsProvider";

const FEATURED_GAMES: string[] = [];
const PRIMARY_GAMES_LIMIT = 5;
const QUEST_CLAIM_DAYS = 5;

const formatClaimableDate = (endDate: string): string => {
  const startDate = formatDateOnly(new Date(endDate));
  const endClaimDate = formatDateOnly(
    addDays(new Date(endDate), QUEST_CLAIM_DAYS),
  );
  return `${startDate} – ${endClaimDate}`;
};

const formatCompletionPeriod = (startDate: string, endDate: string): string => {
  return `${formatDateOnly(new Date(startDate))} and ${formatDateOnly(new Date(endDate))}`;
};

const isQuestEnded = (endDate?: string): boolean => {
  return endDate ? isPast(new Date(endDate)) : false;
};

const isQuestAvailable = (quest: GameQuest): boolean => {
  if (isQuestEnded(quest.endDate)) {
    return false;
  }
  if (!quest.canStillCompleteThisPeriod) {
    return false;
  }
  if (quest.refreshPeriodHours && quest.nextAvailableAt) {
    if (new Date(quest.nextAvailableAt) > new Date()) {
      return false;
    }
  }
  return true;
};

const hasAvailableQuests = (quests: GameQuest[]): boolean => {
  return quests.some((quest) => isQuestAvailable(quest));
};

const OpeningSentence = (
  isLiveProgressKey: boolean | undefined,
  userStats: PredictedStatsResponse,
): React.ReactNode => {
  const keyLabel =
    isLiveProgressKey && userStats?.hasKeyForSweepstake
      ? "YOUR KEY"
      : "YOUR NEXT KEY";

  const subText =
    isLiveProgressKey && !userStats?.hasKeyForSweepstake
      ? "Claim your Key now and continue questing to reach higher tiers!"
      : "Continue questing to reach higher tiers!";

  return (
    <Stack spacing={0.5}>
      <Typography
        variant="body2"
        sx={{ color: onDarkBase.color.text.status.guidance.primary }}
      >
        Follower Bonus: For the next 90 days, the more games you are following
        increases your chances to earn a weekly points bonus.
      </Typography>
      <Box>
        {keyLabel}:{" "}
        <RarityText
          rarity={userStats?.predictedRarity}
          sx={{ textTransform: "uppercase" }}
        />
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: onDarkBase.color.text.caption.secondary,
          fontSize: { xs: "12px", md: "12px" },
          lineHeight: 1.4,
        }}
      >
        {subText}
      </Typography>
    </Stack>
  );
};

interface ProgressBarDropdownContentProps {
  userStats: PredictedStatsResponse;
  rarity: Rarity;
  onClose: () => void;
}

export function ProgressBarDropdownContent({
  userStats,
}: ProgressBarDropdownContentProps) {
  const { sweepstake } = useWeeklyRewards();
  const isLiveProgressKey = sweepstake?.current?.isLiveProgressKey;
  const [showAllIncomplete, setShowAllIncomplete] = useState(false);
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
  const claimDate = sweepstake?.current?.calculationPeriodEndDate
    ? `${formatDateStr(sweepstake?.current?.calculationPeriodEndDate)} `
    : `${formatDateStr(sweepstake?.next?.startDate ?? "Thursday")}`;

  const gameList = useMemo(() => {
    let sortedGames = userStats.games.sort((a, b) => {
      const aIsFeatured = FEATURED_GAMES.includes(a.name);
      const bIsFeatured = FEATURED_GAMES.includes(b.name);

      // 1. Featured games go to the top
      if (aIsFeatured && !bIsFeatured) return -1;
      if (!aIsFeatured && bIsFeatured) return 1;

      // 2. Filter quests that can still be completed
      const aCompletableQuests = a.quests.filter(
        (quest) => quest.canStillCompleteThisPeriod,
      );
      const bCompletableQuests = b.quests.filter(
        (quest) => quest.canStillCompleteThisPeriod,
      );

      // 3. Check the highest weekly points for quests - game with highest single quest comes first
      const aMaxWeeklyPoints = Math.max(
        ...aCompletableQuests.map((q) => q.weeklyPoints || 0),
        0,
      );
      const bMaxWeeklyPoints = Math.max(
        ...bCompletableQuests.map((q) => q.weeklyPoints || 0),
        0,
      );

      if (aMaxWeeklyPoints !== bMaxWeeklyPoints) {
        return bMaxWeeklyPoints - aMaxWeeklyPoints; // Higher max points first
      }

      // 4. If multiple games have the same highest weekly points, sum of all weekly points for each game
      const aTotalWeeklyPoints = aCompletableQuests.reduce(
        (sum, quest) => sum + (quest.weeklyPoints || 0),
        0,
      );
      const bTotalWeeklyPoints = bCompletableQuests.reduce(
        (sum, quest) => sum + (quest.weeklyPoints || 0),
        0,
      );

      if (aTotalWeeklyPoints !== bTotalWeeklyPoints) {
        return bTotalWeeklyPoints - aTotalWeeklyPoints;
      }

      // 5. Alphabetically by game name
      return a.name.localeCompare(b.name);
    });

    if (showOnlyAvailable) {
      sortedGames = sortedGames.filter((game) =>
        hasAvailableQuests(game.quests),
      );
    }
    return sortedGames;
  }, [userStats.games, showOnlyAvailable]);

  const displayedGames = showAllIncomplete
    ? gameList
    : gameList.slice(0, PRIMARY_GAMES_LIMIT);

  const hasMoreGames = gameList.length > PRIMARY_GAMES_LIMIT;

  return (
    <Stack spacing={1} sx={{ backgroundColor: onDarkBase.color.neutral[900] }}>
      {/* Header Section */}
      <Stack spacing={{ xs: 1, md: 1.0 }}>
        <Stack
          justifyContent="space-between"
          direction={{ xs: "column", md: "row" }}
          gap={{ xs: "6px", md: "12px" }}
          spacing={{ xs: 1, md: 0 }}
        >
          <Typography
            sx={{
              typography: { xs: "h5", md: "h4" },
              fontSize: { xs: "16px", md: undefined },
              fontWeight: { xs: 600, md: undefined },
            }}
          >
            <Stack
              direction="row"
              gap={{ xs: "6px", md: "8px" }}
              alignItems="center"
            >
              <Box sx={{ width: { xs: "20px", md: "24px" } }}>
                <HeroCard.Chip.Icon icon="Trophy" variant="bold" size="large" />
              </Box>
              {userStats?.weeklyDrawName}
            </Stack>
          </Typography>

          <Stack
            direction={{ xs: "row", md: "column" }}
            gap={{ xs: "6px", md: "0px" }}
            alignItems={{ xs: "center", md: "flex-end" }}
            justifyContent={{ xs: "space-between", md: "flex-end" }}
          >
            <Stack
              direction="row"
              gap="4px"
              alignItems="center"
              justifyContent={{ xs: "flex-start", md: "flex-end" }}
            >
              <TrophyIcon
                size={16}
                style={{
                  color: onDarkBase.color.text.status.guidance.primary,
                }}
              />
              <Typography
                sx={{
                  typography: { xs: "h5", md: "h4" },
                  textAlign: { xs: "left", md: "right" },
                  color: onDarkBase.color.text.status.guidance.primary,
                  fontSize: { xs: "16px", md: undefined },
                  fontWeight: { xs: 600, md: undefined },
                }}
              >
                {userStats?.weeklyPoints || 0}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: onDarkBase.color.text.caption.secondary,
                  fontSize: { xs: "12px", md: "12px" },
                  display: { xs: "block", md: "none" },
                }}
              >
                Weekly Points
              </Typography>
            </Stack>
            <Typography
              align="right"
              sx={{
                fontSize: "12px",
                color: onDarkBase.color.text.caption.secondary,
                display: { xs: "none", md: "block" },
              }}
            >
              Weekly Points
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      {/* Key Information Section */}
      <Stack spacing={{ xs: 0.5, md: 0.25 }}>
        <Stack direction="row" gap={{ xs: "6px", md: "8px" }} flexWrap="wrap">
          <Typography
            sx={{
              typography: { xs: "body1", md: "h6" },
              fontSize: { xs: "14px", md: undefined },
              fontWeight: { xs: 500, md: undefined },
            }}
          >
            {OpeningSentence(isLiveProgressKey, userStats)}
          </Typography>
        </Stack>
      </Stack>

      {/* Timing Information Section */}
      <Stack spacing={{ xs: 0.5, md: 0.5 }}>
        <Stack
          direction="row"
          gap="4px"
          alignItems={{ xs: "flex-start", md: "center" }}
          flexWrap={{ xs: "wrap", md: "nowrap" }}
        >
          <Typography
            variant="body2"
            sx={{
              color: onDarkBase.color.text.caption.secondary,
              fontSize: { xs: "12px", md: "12px" },
            }}
          >
            {isLiveProgressKey ? "Unlock Rewards starting on: " : "Claimable: "}
          </Typography>
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <HeroCard.Chip.Icon icon="TimeFast" variant="bold" size="large" />
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: onDarkBase.color.text.caption.secondary,
              fontSize: { xs: "12px", md: "12px" },
              flex: { xs: 1, md: "none" },
            }}
          >
            {" "}
            {isLiveProgressKey
              ? claimDate
              : userStats?.completionPeriodEndDate &&
                formatClaimableDate(userStats.completionPeriodEndDate)}
          </Typography>
        </Stack>

        <Typography
          variant="body2"
          sx={{
            color: onDarkBase.color.text.caption.secondary,
            fontSize: { xs: "12px", md: "12px" },
            lineHeight: { xs: 1.4, md: 1.4 },
          }}
        >
          Only quests completed between{" "}
          <Typography
            variant="body2"
            fontWeight="bold"
            sx={{
              color: "#fff",
              fontSize: { xs: "12px", md: "12px" },
            }}
            component="span"
          >
            {userStats?.completionPeriodStartDate &&
              userStats?.completionPeriodEndDate &&
              formatCompletionPeriod(
                userStats.completionPeriodStartDate,
                userStats.completionPeriodEndDate,
              )}
          </Typography>{" "}
          {isLiveProgressKey
            ? "count toward this Key. Your tier updates in real time."
            : "count toward your next Key. Your tier updates in real time, but your final Key may differ."}
        </Typography>
      </Stack>

      {/* Games Section */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent={{ xs: "flex-start", md: "space-between" }}
        gap={{ xs: "12px", md: "8px" }}
      >
        <Typography
          sx={{
            typography: { xs: "h6", md: "h5" },
            fontSize: { xs: "16px", md: undefined },
            fontWeight: { xs: 600, md: undefined },
          }}
        >
          <Stack
            direction="row"
            gap={{ xs: "6px", md: "8px" }}
            alignItems="center"
          >
            <Box sx={{ width: { xs: "18px", md: "20px" } }}>
              <HeroCard.Chip.Icon icon="PlayGame" variant="bold" size="large" />
            </Box>
            Best Games to Play
          </Stack>
        </Typography>
        <Stack direction="row" gap="8px" alignItems="center">
          <Checkbox
            checked={showOnlyAvailable}
            onCheckedChange={(checked) =>
              setShowOnlyAvailable(checked === true)
            }
          />
          <Typography
            variant="body2"
            sx={{ fontSize: { xs: "14px", md: undefined } }}
          >
            Available Quests
          </Typography>
        </Stack>
      </Stack>

      {gameList.length > 0 && (
        <Stack
          spacing={{ xs: 1, md: 2 }}
          sx={{
            width: "100%",
          }}
        >
          <Stack spacing={{ xs: 0.75, md: 1 }}>
            {displayedGames.map((game: Game) => {
              const filteredQuests = showOnlyAvailable
                ? game.quests.filter((quest: GameQuest) =>
                    isQuestAvailable(quest),
                  )
                : game.quests;

              return (
                <GameQuestsCard
                  key={game.id}
                  gameName={game.name}
                  gameIcon={game.icon}
                  gameSlug={game.slug}
                  featured={FEATURED_GAMES.includes(game.name)}
                  gameId={game.id}
                  quests={filteredQuests}
                />
              );
            })}
          </Stack>
        </Stack>
      )}

      {hasMoreGames && !showAllIncomplete && (
        <Link
          variant="body2"
          sx={{
            cursor: "pointer",
            textAlign: "center",
            textDecoration: "none",
            fontSize: { xs: "14px", md: "16px" },
            padding: { xs: "2px", md: "12px" },
            fontWeight: 500,
          }}
          onClick={() => setShowAllIncomplete(true)}
        >
          View More
        </Link>
      )}
    </Stack>
  );
}
